import React, { useMemo, useState } from 'react';
import styles from 'LearningManager/styles/CreateAssignment.module.scss';
import {
	SetupOptions,
	AssignmentSetupType,
	quickSetupSteps,
	customSetupSteps,
	getScheduledEditSteps
} from 'LearningManager/constants/createAssignment.constant';
import SetupCard from 'LearningManager/components/SetupCard';
import { Icon, Notification } from 'Components';
import { useHistory, useLocation } from 'react-router-dom';
import Stepper from 'LearningManager/components/Stepper';
import {
	StepperProvider,
	useStepper
} from 'LearningManager/hooks/Stepper.context';
import CreateExitModal from 'LearningManager/components/CreateExitModal';
import Button from 'LearningManager/components/Button';
import AssignmentSuccessModal from 'LearningManager/components/AssignmentSuccessModal';
import classNames from 'classnames';
import moment from 'moment';
import { useStores } from 'Hooks/useStore';
import { LearningPathPayload } from 'Services/Api/LearningManager/Types';
import {
	AssignmentActivity,
	AssignmentExpirationType,
	AssignmentExpirationUnit,
	AssignmentSourcePayload,
	AssignmentSourceLimitedUpdatePayload
} from 'Services/Api/AssignmentSources/Types';

// The Configure step's "Certification Duration" options map onto the API's
// expiration fields: "One year" is an interval, "Custom expiration" a date,
// "Never expires" none. "One year" is the only interval the step offers, so
// its value/unit are fixed rather than user-entered.
const CERT_DURATION_INTERVAL_VALUE = 1;
const CERT_DURATION_INTERVAL_UNIT: AssignmentExpirationUnit = 'year';

export const useQuery = () => {
	const location = useLocation();
	return useMemo(() => new URLSearchParams(location.search), [
		location.search
	]);
};

const CreateAssignment: React.FC = () => {
	const query = useQuery();

	const currentSetupType = query.get(
		'setupType'
	) as AssignmentSetupType | null;
	const isScheduled = query.get('isScheduled') === 'true';

	const steps = (() => {
		if (!currentSetupType) return null;
		if (isScheduled) return getScheduledEditSteps(currentSetupType);
		if (currentSetupType === 'Quick Setup') return quickSetupSteps;
		if (currentSetupType === 'Custom Setup') return customSetupSteps;
		return null;
	})();

	return (
		<StepperProvider>
			<div id="lms-ca-screen" className={styles.fullScreen}>
				<Header />
				<div id="lms-ca-main" className={styles.root}>
					<Stepper />
					<div id="lms-ca-step-body" className={styles.mainContainer}>
						<StepRenderer
							currentSetupType={currentSetupType}
							steps={steps}
						/>
					</div>
					<NavigationButtons steps={steps} />
				</div>
			</div>
		</StepperProvider>
	);
};

interface StepRendererProps {
	currentSetupType: AssignmentSetupType | null;
	steps: typeof quickSetupSteps | typeof customSetupSteps | null;
}

const StepRenderer: React.FC<StepRendererProps> = ({
	currentSetupType,
	steps
}) => {
	const { current } = useStepper();
	const CurrentStep = steps?.[current]?.component;

	if (!currentSetupType || !CurrentStep) {
		return <SelectCourse />;
	}

	return <CurrentStep />;
};

const Header: React.FC = () => {
	const history = useHistory();
	const query = useQuery();
	const [showModal, setShowModal] = useState(false);
	const isEditMode = Boolean(query.get('assignmentId'));

	return (
		<div id="lms-ca-header" className={styles.headerWrapper}>
			<div className={styles.header}>
				<div
					id="lms-ca-header-title"
					className={styles.header__heading}
				>
					{isEditMode ? 'Edit Assignments' : 'Create Assignment'}
				</div>
				<div id="lms-ca-header-desc" className={styles.header__desc}>
					{isEditMode
						? 'Edit training and course assignments'
						: 'Quickly assign training to your team'}
				</div>
			</div>

			<Icon
				id="lms-ca-header-close"
				type="close"
				role="img"
				onClick={() => setShowModal(true)}
			/>

			<CreateExitModal
				visible={showModal}
				onCancel={() => setShowModal(false)}
				onExit={() => history.push('/assignments')}
			/>
		</div>
	);
};

const SelectCourse: React.FC = () => {
	const { setupType, setSetupType } = useStepper();

	return (
		<div id="lms-ca-select-course" className={styles.selectCourse}>
			<div className={styles.selectCourse__wrapper}>
				<div
					id="lms-ca-select-course-heading"
					className={styles.selectCourse__heading}
				>
					Select Courses
				</div>
				<div
					id="lms-ca-select-course-desc"
					className={styles.selectCourse__desc}
				>
					Choose from among Venza's industry-leading course offerings
				</div>
			</div>

			<div id="lms-ca-setup-container" className={styles.setupContainer}>
				{SetupOptions.map((opt) => (
					<SetupCard
						key={opt.title}
						id={`lms-ca-setup-card-${opt.title
							.toLowerCase()
							.replace(/\s+/g, '-')}`}
						{...opt}
						isActive={setupType === opt.title}
						onClick={() =>
							setSetupType(opt.title as AssignmentSetupType)
						}
					/>
				))}
			</div>
		</div>
	);
};

interface NavigationProps {
	steps: typeof quickSetupSteps | typeof customSetupSteps | null;
}

const NavigationButtons: React.FC<NavigationProps> = ({ steps }) => {
	const history = useHistory();
	const location = useLocation();
	const { learningManagerStore, authStore } = useStores();
	const {
		current,
		setupType,
		steps: stepItems,
		draft,
		next,
		back,
		isScheduledEdit
	} = useStepper();
	const [showSuccessModal, setShowSuccessModal] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const query = useQuery();

	const currentSetupType = query.get('setupType');

	// The auth store holds the tenant's zee number as a string; the LMS
	// payload wants it as a number.
	const zeeNumber = authStore.currentUser?.userPool?.zeeNumber;
	const tenantId = Number(zeeNumber);
	const hasTenantId = Boolean(zeeNumber) && Number.isFinite(tenantId);

	const buildPayload = (): LearningPathPayload => {
		const courseIds =
			setupType === 'Quick Setup'
				? draft.quickSetup.selectedLearningPathCourseIds
				: [
						...draft.customSetup.selectedLearningPathCourseIds,
						...draft.customSetup.selectedIndividualCourseIds
				  ];

		return {
			name: draft.configure.assignmentName,
			description: draft.configure.assignmentName,
			is_required:
				draft.configure.assignmentType === 'one-time-assignment',
			order: 1,
			activities: courseIds.map((activity_id, index) => ({
				activity_id,
				is_required: true,
				order: index + 1
			}))
		};
	};

	// Deduplicate activities (an activity can belong to multiple learning paths).
	const dedupeActivities = (
		items: AssignmentActivity[]
	): AssignmentActivity[] => {
		const byId = new Map<number, AssignmentActivity>();
		items.forEach((item) => {
			if (!byId.has(item.activity_id)) byId.set(item.activity_id, item);
		});
		return Array.from(byId.values());
	};

	// Quick Setup: all reviewed learning paths + their (flattened) activities,
	// org-wide scope (targets the whole organization)
	const buildAssignmentSourcePayload = (): AssignmentSourcePayload => ({
		...buildConfigureBase(),
		activities: dedupeActivities(
			learningManagerStore.learningPaths.flatMap((lp) =>
				(lp.activities ?? []).map((a) => ({
					activity_id: a.activity_id,
					is_required: a.is_required
				}))
			)
		),
		learning_paths: learningManagerStore.learningPaths.map((lp) => ({
			learning_path_id: lp.id,
			is_required: lp.is_required
		})),
		scopes: { group_labels: ['Organization'] }
	});

	const buildConfigureBase = () => {
		const { configure } = draft;

		// Backend wants ISO 8601 with an explicit offset and no milliseconds
		// (e.g. 2026-07-25T00:00:00+00:00), not the `.000Z` form.
		const toIso = (date: string) =>
			moment.utc(date).format('YYYY-MM-DDTHH:mm:ssZ');

		// Backend requires the start date to be in the future, so "immediate"
		// maps to the earliest allowed start (tomorrow).
		const startDate =
			configure.assignmentStart === 'schedule' &&
			configure.assignmentStartDate
				? configure.assignmentStartDate
				: moment().add(1, 'day').format('YYYY-MM-DD');

		const dueDate =
			configure.dueDate === 'custom' && configure.dueDateValue
				? configure.dueDateValue
				: null;

		const expirationType: AssignmentExpirationType =
			configure.certDuration === 'one-year'
				? 'interval'
				: configure.certDuration === 'custom'
				? 'date'
				: 'none';

		// Only the fields the selected expiration type actually uses are sent;
		// the rest are nulled out.
		const isInterval = expirationType === 'interval';
		const isDate = expirationType === 'date';

		return {
			name: configure.assignmentName,
			tenant_id: tenantId,
			start_date: toIso(startDate),
			due_date: dueDate ? toIso(dueDate) : null,
			expiration_type: expirationType,
			expiration_interval_value: isInterval
				? CERT_DURATION_INTERVAL_VALUE
				: null,
			expiration_interval_unit: isInterval
				? CERT_DURATION_INTERVAL_UNIT
				: null,
			expiration_date:
				isDate && configure.certExpirationDate
					? moment(configure.certExpirationDate).format('MM-DD')
					: null,
			is_assigned_to_future_users:
				configure.assignmentType === 'ongoing-assignment',
			is_replacing: false
		};
	};

	const buildCustomAssignmentSourcePayload = (): AssignmentSourcePayload => {
		const { customSetup } = draft;

		const scopes: AssignmentSourcePayload['scopes'] = (() => {
			switch (customSetup.selectedAssignUsersOptionId) {
				case 214:
					return {
						group_ids: customSetup.selectedOrganizations.map(
							(o) => o.id
						)
					};
				case 35:
					return {
						group_ids: customSetup.selectedProperties.map((p) => p.id)
					};
				case 634:
					return {
						role_ids: customSetup.selectedRoles.map((r) => r.id)
					};
				case 676:
					return {
						group_ids: customSetup.selectedGroups.map((g) => g.id)
					};
				case 784:
					return {
						user_ids: customSetup.selectedIndividualUsers.map(
							(u) => u.id
						)
					};
				default:
					return {};
			}
		})();

		return {
			...buildConfigureBase(),
			activities: dedupeActivities([
				...learningManagerStore.learningPaths
					.filter((lp) =>
						customSetup.selectedLearningPathCourseIds.includes(
							lp.id
						)
					)
					.flatMap((lp) =>
						(lp.activities ?? []).map((a) => ({
							activity_id: a.activity_id,
							is_required: a.is_required
						}))
					),
				...customSetup.selectedIndividualCourseIds.map((id) => ({
					activity_id: id,
					is_required: true
				}))
			]),
			learning_paths: customSetup.selectedLearningPathCourseIds.map(
				(id) => ({ learning_path_id: id, is_required: true })
			),
			scopes
		};
	};

	// Limited update: only name + due date + expiration (after start date has passed)
	const buildLimitedUpdatePayload = (): AssignmentSourceLimitedUpdatePayload => {
		const base = buildConfigureBase();
		return {
			name: base.name,
			due_date: base.due_date,
			expiration_type: base.expiration_type,
			expiration_interval_value: base.expiration_interval_value,
			expiration_interval_unit: base.expiration_interval_unit,
			expiration_date: base.expiration_date
		};
	};

	const onBack = () => {
		if (current > 0) {
			back();
		}
	};

	const onNext = async () => {
		if (!currentSetupType) {
			if (!setupType) return;

			const params = new URLSearchParams(location.search);
			params.set('setupType', setupType);

			history.push({
				pathname: location.pathname,
				search: params.toString()
			});
			return;
		}

		if (steps && current < steps.length - 1) {
			next();
			return;
		}

		if (currentSetupType && current === stepItems.length - 1) {
			const assignmentId = query.get('assignmentId');
			// Every payload except the limited update carries tenant_id, and we
			// must never post one against the wrong customer.
			const needsTenantId = !assignmentId || isScheduledEdit;
			if (needsTenantId && !hasTenantId) {
				Notification.error({
					description:
						'Could not determine your tenant. Please refresh and try again.'
				});
				return;
			}
			setIsSubmitting(true);
			try {
				if (assignmentId) {
					if (isScheduledEdit) {
						// Scheduled (start date in future) → full update allowed
						const payload =
							setupType === 'Quick Setup'
								? buildAssignmentSourcePayload()
								: buildCustomAssignmentSourcePayload();
						await learningManagerStore.updateAssignmentSource(
							Number(assignmentId),
							payload
						);
					} else {
						// Active (start date passed) → limited update only
						await learningManagerStore.updateAssignmentSource(
							Number(assignmentId),
							buildLimitedUpdatePayload()
						);
					}
				} else if (setupType === 'Quick Setup') {
					await learningManagerStore.createAssignmentSource(
						buildAssignmentSourcePayload()
					);
				} else {
					// Custom Setup
					await learningManagerStore.createAssignmentSource(
						buildCustomAssignmentSourcePayload()
					);
				}
				setShowSuccessModal(true);
			} catch {
				Notification.error({
					description: 'Something went wrong. Please try again.'
				});
			} finally {
				setIsSubmitting(false);
			}
		}
	};

	const redirectToCourses = () => {
		history.push('/assignments');
	};

	const isEditMode = Boolean(query.get('assignmentId'));
	const isLastStep = currentSetupType && current === stepItems.length - 1;
	const nextLabel = isLastStep ? (isEditMode ? 'Confirm & Update' : 'Confirm & Assign') : 'Next';
	const hideBackButton = currentSetupType === 'Quick Setup' && current === 0;

	// A custom certification duration is an expiration date, so it isn't
	// complete until that date is picked.
	const hasCertDuration =
		draft.configure.certDuration === 'custom'
			? Boolean(draft.configure.certExpirationDate)
			: Boolean(draft.configure.certDuration);

	const hasConfigureData = Boolean(
		draft.configure.assignmentName.trim() &&
			hasCertDuration &&
			(isEditMode || draft.configure.assignmentStart) &&
			draft.configure.dueDate &&
			draft.configure.assignmentType
	);

	const hasCustomSelection =
		draft.customSetup.selectedLearningPathCourseIds.length > 0 ||
		draft.customSetup.selectedIndividualCourseIds.length > 0;

	const hasAssignUsersSelection = (() => {
		switch (draft.customSetup.selectedAssignUsersOptionId) {
			case 214:
				return draft.customSetup.selectedOrganizations.length > 0;
			case 35:
				return draft.customSetup.selectedProperties.length > 0;
			case 634:
				return draft.customSetup.selectedRoles.length > 0;
			case 676:
				return draft.customSetup.selectedGroups.length > 0;
			case 784:
				return draft.customSetup.selectedIndividualUsers.length > 0;
			default:
				return false;
		}
	})();

	const isNextDisabled = (() => {
		if (!currentSetupType) return false;

		// Scheduled edit: only Configure (step 0) needs validation
		if (isScheduledEdit) {
			if (current === 0) return !hasConfigureData;
			return false;
		}

		if (setupType === 'Quick Setup') {
			if (current === 1) return !hasConfigureData;
			return false;
		}

		if (setupType === 'Custom Setup') {
			if (current === 0) return !hasCustomSelection;
			if (current === 1) return !hasAssignUsersSelection;
			if (current === 2) return !hasConfigureData;
			return false;
		}

		return false;
	})();

	return (
		<div
			id="lms-ca-nav"
			className={classNames(styles.navigationButtons, {
				[styles['navigationButtons--flex-end']]: hideBackButton
			})}
		>
			{!hideBackButton && (
				<Button
					id="lms-ca-nav-back"
					variant="border"
					size="small"
					onClick={onBack}
					disabled={current === 0}
				>
					<Icon
						type="chevronUp"
						role="img"
						style={{ rotate: '-90deg' }}
					/>
					Back
				</Button>
			)}

			<Button
				id="lms-ca-nav-next"
				size="small"
				onClick={onNext}
				className={isLastStep ? 'cta-prominent' : undefined}
				disabled={isNextDisabled || isSubmitting}
			>
				{nextLabel}{' '}
				{!isLastStep && (
					<Icon
						type="chevronUp"
						role="img"
						style={{ rotate: '90deg' }}
					/>
				)}
			</Button>
			<AssignmentSuccessModal
				visible={showSuccessModal}
				onClose={redirectToCourses}
				isEditMode={isEditMode}
			/>
		</div>
	);
};

export default CreateAssignment;
